import { ComponentProps, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Layer, Source } from 'react-map-gl';
import { Feature, Point } from 'geojson';
import { ExpressionSpecification, MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import { isFirefox, isIos, isSafari, useClientNavigationPushState, useGroupHover } from '@alltrails/core';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { type POISearchResult, poiSubTypes } from '@alltrails/search/types/algoliaResultTypes';
import { useExperiments } from '@alltrails/experiments';
import useMap from '../../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { updateHoveredPOI, updateCursor, updateRenderedPOIs, updateActivePOI } from '../../../redux/reducer';
import { serializedCoordinatesToCoordinates } from '../../../utils/serializedCoordinates';
import { defaultPOIPinLayerPaintProps, getLayerBeforeId, poiPinsId } from '../../../utils/layers';
import { imageUrlToBase64 } from '../../../utils/imageUrlToBase64';
import useSvgImages, { preloadSvg, SvgDefinition } from '../../../utils/useSvgImages';
import { poiPinHeightXs, poiPinImageWidthXs } from '../../../utils/svgDefinitions/svgMarkerComponents';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import createPOIIconPinSvg, { imageNamePrefix as iconPrefix } from '../../../utils/svgDefinitions/createPOIIconPinSvg';
import createPOIPhotoPinSvg, { getPOIPhotoName, imageNamePrefix as photoPrefix } from '../../../utils/svgDefinitions/createPOIPhotoPinSvg';
import { useDelayedOnStyleChange } from '../../../hooks/useDelayedOnStyleChange';
import pinZoomParkAndPOIMaxCount from '../../../utils/pinZoomParkAndPOIMaxCount';
import { getZoomBucket } from '../../..';
import { defaultPOIPinXSLayoutProps } from './constants';
import HoveredPOIPin from './POIPopupPin';
import type POIResultProperties from './POIResultProperties';

const defaultClusterMaxZoom = 15;

function getMaxPOIPinCount(zoomLevel: number, isMobile: boolean, hasClusteringExperiment: boolean = false) {
  if (hasClusteringExperiment) {
    const zoomBucket = getZoomBucket(zoomLevel);
    const { maxPOIs } = pinZoomParkAndPOIMaxCount(zoomBucket);
    return maxPOIs;
  }
  if (zoomLevel >= defaultClusterMaxZoom) {
    return undefined;
  }

  return isMobile ? 5 : 10;
}

function poiToPOIFeature(serializedCoordinates: string, poi: POISearchResult): Feature<Point, POIResultProperties> {
  const { lat, lng } = serializedCoordinatesToCoordinates(serializedCoordinates);
  return {
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {
      hasPhotos: !!poi.photos_count,
      id: poi.ID,
      name: poi.name,
      popularity: poi.popularity ?? 0,
      serializedCoordinates,
      type: poi.subtype
    },
    type: 'Feature'
  };
}

export const poiSourceId = 'poi-results-source';

type POIResultsProps = {
  getPOIUrl?: (poi: POISearchResult) => string;
  onPOIClick?: (poi: POISearchResult) => void;
  onPOICardViewed?: ComponentProps<typeof HoveredPOIPin>['onPOICardViewed'];
};

const POIResults = ({ getPOIUrl, onPOIClick, onPOICardViewed }: POIResultsProps) => {
  const poiImageFetched = useRef<Set<number>>(new Set());
  const newFeatureSet = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const pushState = useClientNavigationPushState();
  const map = useMap();
  const isMobile = useIsMobileSizedScreen();
  const hasClusteringExperiment = useExperiments()?.['web-discover-explore-clustering-rm']?.value === 'on';
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const { activePOI, hoveredPOI, interactionBehavior, poiResults, respectPOIRenderLimit } = useAppSelector(state => ({
    activePOI: state.map.activePOI,
    hoveredPOI: state.map.hoveredPOI,
    interactionBehavior: state.map.poiInteractionBehavior,
    poiResults: state.map.poiResults,
    respectPOIRenderLimit: state.map.respectPOIRenderLimit
  }));

  const features = useMemo(() => {
    if (!poiResults) {
      return [];
    }

    newFeatureSet.current = true;
    const maxPOIPinCount = respectPOIRenderLimit ? getMaxPOIPinCount(map?.getZoom() ?? 0, isMobile, hasClusteringExperiment) : undefined;
    if (maxPOIPinCount !== undefined) {
      let poiFeatures: Feature<Point, POIResultProperties>[] = [];
      for (let [serializedCoordinates, poi] of Object.entries(poiResults)) {
        if (!poi?._geoloc || (!poi.photos_count && map && map.getZoom() < 8)) {
          continue;
        }

        poiFeatures.push(poiToPOIFeature(serializedCoordinates, poi));
        if (poiFeatures.length >= maxPOIPinCount) {
          break;
        }
      }
      return poiFeatures;
    }

    return Object.keys(poiResults).reduce<Feature<Point, POIResultProperties>[]>((allFeatures, serializedCoordinates) => {
      const poi = poiResults[serializedCoordinates];
      if (!poi?._geoloc || (!poi.photos_count && map && map.getZoom() < 8)) {
        return allFeatures;
      }
      return [...allFeatures, poiToPOIFeature(serializedCoordinates, poi)];
    }, []);
  }, [hasClusteringExperiment, isMobile, map, poiResults, respectPOIRenderLimit]);

  const defaultSvgDefinitions = useMemo(() => {
    const definitions: SvgDefinition[] = poiSubTypes.reduce((defs, poiType) => {
      defs.push(createPOIIconPinSvg(poiType, 'xs'));
      if (interactionBehavior === 'enlarge') {
        defs.push(createPOIIconPinSvg(poiType, 'md'));
      }
      return defs;
    }, [] as SvgDefinition[]);
    return definitions;
  }, [interactionBehavior]);
  const { imagesAreLoaded, loadImage } = useSvgImages(defaultSvgDefinitions);

  const onHover = useCallback(
    (properties?: POIResultProperties & { cluster_id?: number }) => {
      if (properties) {
        const serializedCoordinates = properties.serializedCoordinates;
        dispatch(updateHoveredPOI(poiResults?.[serializedCoordinates]));
        dispatch(updateCursor('pointer'));
      }
    },
    [dispatch, poiResults]
  );

  const onHoverEnd = useCallback(() => {
    dispatch(updateHoveredPOI(undefined));
    dispatch(updateCursor(''));
  }, [dispatch]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true /* isMouseMove */, 50);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (onMouseEnter && event.features) {
        const marker = event.features[0];
        onMouseEnter(marker.properties);
      }
    },
    [onMouseEnter]
  );

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (event.features) {
        event.preventDefault(); // Allows us to detect when a user clicks on the map but NOT on this layer
        const marker = event.features[0];
        if (poiResults) {
          const { cluster, serializedCoordinates } = marker.properties;
          const clusterCoordinates = cluster ? { lat: marker.geometry.coordinates[1], lng: marker.geometry.coordinates[0] } : undefined;
          const poi = poiResults[serializedCoordinates];
          onPOIClick?.(poi);
          if (poi.ID === activePOI?.ID) {
            // if the already active POI is clicked, remove the active state
            dispatch(updateActivePOI(undefined));
            dispatch(updateHoveredPOI(undefined));
            dispatch(updateCursor(''));
          } else {
            dispatch(updateActivePOI({ ...poi, clusterCoordinates }));
          }
          if (getPOIUrl) {
            pushState(getPOIUrl(poi));
          }
        }
      }
    },
    [activePOI, dispatch, getPOIUrl, onPOIClick, poiResults, pushState]
  );

  const loadPOIPins = useCallback(async () => {
    features.forEach(async feature => {
      const poi = poiResults?.[feature.properties.serializedCoordinates];
      if (poi && poi.photos_count && !poiImageFetched.current.has(poi.ID)) {
        let imageStr;
        try {
          imageStr = await imageUrlToBase64(
            `/api/alltrails/points_of_interest/${poi.ID}/profile_photo?size=${poiPinImageWidthXs * 2}w${poiPinHeightXs * 2}h`
          );
        } catch {
          loadImage({ ...createPOIIconPinSvg(poi.subtype, 'xs'), name: getPOIPhotoName(poi.ID, 'xs') });
          return;
        }
        if (!imageStr) return;
        poiImageFetched.current.add(poi.ID);

        const svgDefinition = [
          createPOIPhotoPinSvg(poi.ID, imageStr, 'xs'),
          ...(interactionBehavior === 'enlarge' ? [createPOIPhotoPinSvg(poi.ID, imageStr, 'md')] : [])
        ];
        svgDefinition.forEach(async svgDefinition => {
          const loadPhotoPin = () => {
            loadImage(svgDefinition);
            map?.triggerRepaint();
          };
          if (isSafari() || (isFirefox() && isIos())) {
            // Safari seems to have their own way of handling SVGs, and as a result the `onLoad` event for the image seems to fire prior to the full pin image being loaded.
            await preloadSvg(svgDefinition.svgString).then(() => {
              loadPhotoPin();
            });
          } else {
            loadPhotoPin();
          }
        });
      }
    });
  }, [features, interactionBehavior, loadImage, map, poiResults]);

  const onStyleChange = useCallback(() => {
    poiImageFetched.current = new Set();
    loadPOIPins();
  }, [loadPOIPins]);
  useDelayedOnStyleChange(onStyleChange, loadPOIPins /* onInitialLoad */); // reload dynamic image pins when map style changes

  useEffect(() => {
    registerEventHandler(poiPinsId, 'click', onClick);
    !isMobile && registerEventHandler(poiPinsId, 'mousemove', onMouseMove);
    !isMobile && onMouseLeave && registerEventHandler(poiPinsId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(poiPinsId, 'click', onClick);
      unregisterEventHandler(poiPinsId, 'mousemove', onMouseMove);
      onMouseLeave && unregisterEventHandler(poiPinsId, 'mouseleave', onMouseLeave);
    };
  }, [isMobile, onClick, onMouseEnter, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  useEffect(() => {
    if (!map) return;
    const onIdle = () => {
      if (newFeatureSet.current) {
        const renderedFeatures = map.queryRenderedFeatures({ layers: [poiPinsId] });
        dispatch(updateRenderedPOIs(renderedFeatures.length));
        newFeatureSet.current = false;
      }
    };

    map.on('idle', onIdle);
    return () => {
      map.off('idle', onIdle);
    };
  }, [dispatch, map]);

  useEffect(() => {
    const removeActiveStates = (event: MapMouseEvent | MapTouchEvent) => {
      // We prevent default when a user clicks on this layer
      if (!event.defaultPrevented) {
        dispatch(updateActivePOI(undefined));
        dispatch(updateHoveredPOI(undefined));
      }
    };

    map?.on('click', removeActiveStates);
    map?.on('movestart', removeActiveStates);
    return () => {
      map?.off('click', removeActiveStates);
      map?.off('movestart', removeActiveStates);
    };
  }, [dispatch, map]);

  if (!map || !imagesAreLoaded) {
    return null;
  }

  const poiLayerLayout = {
    ...defaultPOIPinXSLayoutProps,
    'icon-image': [
      'case',
      ['==', ['get', 'hasPhotos'], true],
      [
        'concat',
        `${photoPrefix}-`,
        ['get', 'id'],
        [
          'case',
          [
            'all',
            ['==', interactionBehavior, 'enlarge'],
            ['any', ['==', ['get', 'id'], hoveredPOI?.ID ?? null], ['==', ['get', 'id'], activePOI?.ID ?? null]]
          ],
          '-md',
          '-xs'
        ]
      ],
      [
        'concat',
        `${iconPrefix}-`,
        ['get', 'type'],
        [
          'case',
          [
            'all',
            ['==', interactionBehavior, 'enlarge'],
            ['any', ['==', ['get', 'id'], hoveredPOI?.ID ?? null], ['==', ['get', 'id'], activePOI?.ID ?? null]]
          ],
          '-md',
          '-xs'
        ]
      ]
    ] as ExpressionSpecification,
    'icon-ignore-placement': false,
    'icon-allow-overlap': false,
    'symbol-sort-key': [
      'case',
      [
        'all',
        ['==', interactionBehavior, 'enlarge'],
        ['any', ['==', ['get', 'id'], hoveredPOI?.ID ?? null], ['==', ['get', 'id'], activePOI?.ID ?? null]]
      ],
      1,
      ['-', 100, ['get', 'popularity']]
    ] as ExpressionSpecification
  };

  return (
    <>
      {/* Initial source used to render POI results and clusters */}
      <Source id={poiSourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
        <Layer
          source={poiSourceId}
          id={poiPinsId}
          type="symbol"
          beforeId={getLayerBeforeId(map, poiPinsId)}
          layout={poiLayerLayout}
          paint={defaultPOIPinLayerPaintProps}
        />
      </Source>
      {interactionBehavior === 'popup' && (
        <HoveredPOIPin
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          getPOIUrl={getPOIUrl}
          onPOICardViewed={onPOICardViewed}
          onPopupClick={onPOIClick}
        />
      )}
    </>
  );
};

export default POIResults;
